<template>
  <div v-if="volunteer && volunteer.friendInfo" class="volunteercardstandard">
    <div class="topsection">
      <div class="circle">
        <friendshipicon
          v-if="friendshipStateOverride"
          :friendname="volunteer.firstName"
          :state="friendshipStateOverride"
        />
        <friendicon
          v-else
          :friendname="volunteer.firstName"
          :image="volunteer.portrait"
          :state="volunteer.friendInfo.standardProfile.onHold ? 6 : volunteer.friendInfo.standardProfile.state"
          :multiple-friendships="volunteer.friendInfo.standardProfile.multipleCustomers"
        />
      </div>
      <div class="details">
        <div class="name">
          <span>{{ volunteer.firstName }} {{ volunteer.lastName }}</span>
          <div class="usericons">
            <font-awesome-icon v-if="!volunteer.isNonIdent" icon="user" :title="$t('customerSearch.isIdent')" />
            <font-awesome-icon v-if="hasMultipleCustomers" icon="users" :title="$t('customerSearch.multipleCustomers')" />
          </div>
        </div>
        <div class="personal">
          <div>{{ genderAgeText }}</div>
          <div>{{ $t('customerSearch.clientTypeWish') }}: {{ customerTypes || '&ndash;' }}</div>
          <div v-if="volunteer.friendInfo.standardProfile.shortDescription" class="short-description">
            <font-awesome-icon icon="quote-left" aria-hidden="true" /><span>{{ volunteer.friendInfo.standardProfile.shortDescription }}</span><font-awesome-icon icon="quote-left" aria-hidden="true" />
          </div>
        </div>
      </div>
      <div v-show="volunteer.friendInfo.lastContacted" class="contacted">
        {{ lastContacted(volunteer.friendInfo.lastContacted) }}
      </div>
      <div class="nofriend">
        {{ daysWithoutFriend(volunteer.friendInfo.standardProfile.lastFriendshipEndDate) }}
      </div>
    </div>

    <div class="properties">
      <div class="row property location">
        <div class="icon">
          <font-awesome-icon icon="map-marker-alt" />
        </div>
        <div class="content">
          <div>{{ $t('profile.address') }}: {{ address || '&ndash;' }}</div>
          <div>{{ $t('customerSearch.myTravelTime') }}: {{ transportationText.toLowerCase() }}</div>
          <div v-if="showMatches">
            {{ matches.travelData.travelDistanceTime }}
          </div>
        </div>
        <div v-if="showMatches" class="match">
          <div v-if="showDistanceWarning">
            <tickcrosswarning v-model="showDistanceWarningValue" />
          </div>
          <div v-else>
            <tickcross v-model="matches.travelData.isMatch" />
          </div>
        </div>
      </div>

      <div class="row property availabilitytimes">
        <div class="icon">
          <font-awesome-icon :icon="['far', 'clock']" />
        </div>
        <div class="content">
          {{ $t('customerSearch.myTimes') }}: <strong>{{ availabilityText || '&ndash;' }}</strong>
        </div>
        <div v-if="showMatches" class="match">
          <tickcross v-model="matches.availabilityTimesMatch" />
        </div>
      </div>

      <div class="row property availabilitytimes">
        <div class="icon">
          <font-awesome-icon :icon="['far', 'clock']" />
        </div>
        <div class="content">
          {{ $t('customerSearch.myFrequency') }}: <strong>{{ availabilityFrequency || '&ndash;' }}</strong>
        </div>
        <div v-if="showMatches" class="match">
          <tickcross v-model="matches.availabilityFrequencyMatch" />
        </div>
      </div>

      <div class="row property genders">
        <div class="icon">
          <font-awesome-icon icon="venus-mars" />
        </div>
        <div class="content">
          <div>{{ $t('customerSearch.am') }} <strong>{{ gender.toLowerCase() || '...?' }}</strong></div>
          <div>{{ $t('customerSearch.forFriend') }}: <strong>{{ genderPreferences || '&ndash;' }}</strong></div>
        </div>
        <div v-if="showMatches" class="match">
          <tickcrosswarning v-model="matches.genderMatch" />
        </div>
      </div>

      <div class="row property age">
        <div class="icon">
          <font-awesome-icon icon="id-badge" />
        </div>
        <div class="content">
          <div>{{ $t('customerSearch.am') }} <strong>{{ age || '?' }} {{ $t('customerSearch.years') }}</strong></div>
          <div>{{ $t('customerSearch.forFriend') }}: <strong>{{ agePreferences || '&ndash;' }}</strong></div>
        </div>
        <div v-if="showMatches" class="match">
          <tickcross v-model="matches.ageMatch" />
        </div>
      </div>

      <div class="row property language">
        <div class="icon">
          <font-awesome-icon :icon="['far', 'comment']" />
        </div>
        <div class="content">
          <div>{{ $t('customerSearch.speak') }}: <strong>{{ normalizeTranslation(volunteer.nativeLanguage) }} ({{ $t('customerSearch.nativeLanguage') }})</strong></div>
          <div v-if="spokenLanguages">
            <strong>{{ spokenLanguages }}</strong>
          </div>
        </div>
        <div v-if="showMatches" class="match">
          <tickcross v-model="matches.languageMatch" />
        </div>
      </div>

      <div class="row property friendshiptypes">
        <div class="icon">
          <font-awesome-icon icon="calendar" />
        </div>
        <div class="content">
          {{ $t('customerSearch.seek') }}: <strong>{{ friendshipTypes || '&ndash;' }}</strong>
        </div>
      </div>

      <div class="row property interest">
        <div class="icon">
          <font-awesome-icon icon="puzzle-piece" />
        </div>
        <div class="content">
          {{ $t('customerSearch.interestedIn') }}: <strong>{{ interests || '&ndash;' }}</strong>
        </div>
      </div>

      <div v-show="volunteer.friendInfo.hasHealthCareTraining" class="row property training">
        <div class="icon">
          <font-awesome-icon icon="plus" />
        </div>
        <div class="content">
          <span v-if="volunteer.friendInfo.healthCareTrainingFreetext">{{ $t('customerSearch.hasHealthCareTraining') }} | {{ volunteer.friendInfo.healthCareTrainingFreetext }}</span>
          <span v-else>{{ $t('customerSearch.unspecifiedHealthcareTraining') }}</span>
        </div>
      </div>
    </div>

    <div class="moreinfo">
      <div class="row">
        <div class="col description">
          <sprcol :heading="$t('profile.notes')">
            <div slot="body">
              <sprprivatenotes v-model="volunteer.friendInfo" endpoint="volunteerupdateprivatenotesstandard" :friend-management-group-id="group.id" :target-id="targetId" />
            </div>
          </sprcol>
        </div>
      </div>
      <div class="row">
        <div class="col contact">
          <sprcol :heading="$t('customerSearch.contactTitle')">
            <div slot="body">
              <p class="smalltext">
                {{ contactDetails }}
              </p>
            </div>
          </sprcol>
        </div>
      </div>
      <div v-if="volunteer.friendInfo.standardProfile.volunteerInterviewed" class="row">
        <div class="col other">
          <sprcol :heading="$t('customerSearch.otherInfoTitle')">
            <div slot="body">
              <dl class="row">
                <dt class="col-12">
                  {{ $t('customerForm.customerInterviewed') }}
                </dt>
                <dd class="col-12">
                  {{ volunteer.friendInfo.standardProfile.volunteerInterviewedDate }} || {{ volunteer.friendInfo.standardProfile.volunteerInterviewedBy }}
                </dd>
              </dl>
            </div>
          </sprcol>
        </div>
      </div>
      <div v-if="isOnHold" class="row">
        <div class="col onhold">
          <sprcol :heading="$t('volunteerForm.onHoldDescription')">
            <div slot="body">
              <dl class="row">
                <dt class="col-12">
                  {{ $t('customerSearch.onHoldUntilDate') }}
                </dt>
                <dd class="col-12">
                  {{ formattedOnHoldUntilDate }}
                </dd>
              </dl>
              <dl class="row">
                <dt class="col-12">
                  {{ $t('customerSearch.onHoldComment') }}
                </dt>
                <dd class="col-12">
                  {{ volunteer.friendInfo.standardProfile.onHoldDetails }}
                </dd>
              </dl>
            </div>
          </sprcol>
        </div>
      </div>
    </div>

    <!-- Actions -->
    <div v-if="loaded" class="row button-row">
      <div v-if="showActions || showEditButton" class="actions">
        <div class="col buttons">
          <volunteerActions
            v-model="volunteer"
            :group="group"
            :loaded="loaded"
            :show-edit-button="showEditButton"
            :show-all-buttons="showActions"
            @saveVolunteer="saveVolunteer"
            @toggleActivity="toggleActivity"
            @moveVolunteer="moveVolunteer"
          />
        </div>
      </div>
    </div>
    <div v-if="isSending" class="text-center">
      <Loading />
    </div>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue'
  import { LocaleMessage } from 'vue-i18n'
  import * as _ from 'lodash'
  import friendicon from './FriendIcon.vue'
  import friendshipicon from './FriendshipIcon.vue'
  import tickcross from '../SPRTickCross.vue'
  import tickcrosswarning from '../SPRTickCrossWarning.vue'
  import sprcol from '../SPRCollapsible.vue'
  import sprprivatenotes from '../SPRPrivateNotes.vue'
  import volunteerActions from './VolunteerActions.vue'
  import Loading from '../SPRLoading.vue'

  import moment from 'moment'

  import enumhelper from '../../mixins/enums.js'
  import datehelper from '../../mixins/dates.js'
  import locationhelper from '../../mixins/locationutils.js'
  import scrollhelper from '../../mixins/scroll.js'
  import addresshelper from '@/helpers/addresshelper'
  import bitmaskhelper from '@/helpers/bitmaskhelper'
  import { UserProfileDto, FriendshipState, FriendManagementDto, CustomerState } from '@/types/index'
  import { YesNoWarning, MatchData } from '@/types/custom'
  import { finnishDateFormat } from '@/helpers/datehelper'

  // the data object
  export interface TemplateComponentData {
    volunteer: UserProfileDto,
    loaded: boolean,
    isSending: boolean
  }

  export default Vue.extend({
    name: 'VolunteerCardStandard',
    components: { friendicon, friendshipicon, tickcross, tickcrosswarning, sprcol, sprprivatenotes, volunteerActions, Loading },
    mixins: [enumhelper,
             datehelper,
             locationhelper,
             scrollhelper],
    props: {
      value: {
        type: Object as () => UserProfileDto,
        required: true
      },
      group: {
        type: Object as () => FriendManagementDto,
        required: true
      },
      showActions: {
        type: Boolean,
        default: () => {
          return true
        }
      },
      showEditButton: {
        type: Boolean,
        default: () => {
          return false
        }
      },
      showMatches: {
        type: Boolean,
        default: () => {
          return false
        }
      },
      matches: {
        type: Object as () => MatchData,
        default: () => {
          return {
            travelData: {
              travelDistanceTime: '',
              isMatch: false,
              isRealDistance: false
            },
            availabilityTimesMatch: false,
            availabilityFrequencyMatch: false,
            genderMatch: YesNoWarning.No,
            ageMatch: false,
            languageMatch: false,
            interestMatch: false
          } as MatchData
        }
      },
      friendshipStateOverride: {
        type: Number as () => FriendshipState,
        required: false,
        default: undefined
      }
    },
    data (): TemplateComponentData {
      return {
        volunteer: this.value,
        loaded: true,
        isSending: false
      }
    },
    computed: {
      isNonIdent (): boolean {
        return this.value.isNonIdent
      },
      hasMultipleCustomers (): boolean {
        return Boolean(this.volunteer?.friendInfo?.standardProfile?.multipleCustomers)
      },
      gender (): string | LocaleMessage {
        // @ts-ignore - mixins not picked up
        return this.getGenderTranslation(this.volunteer.friendInfo.gender)
      },
      isOnHold (): boolean {
        return this.volunteer.friendInfo.standardProfile.state === CustomerState.OnHold
      },
      formattedOnHoldUntilDate (): string {
        if (this.volunteer.friendInfo.standardProfile.onHoldDeadline) {
          const date = this.volunteer.friendInfo.standardProfile.onHoldDeadline
          const formattedDate = moment(date).format('DD.MM.YYYY')

          return formattedDate
        } else {
          return ''
        }
      },
      age (): number {
        // @ts-ignore - mixins not picked up
        return this.calculateAge(this.volunteer.age)
      },
      genderAgeText (): string {
        return [this.gender, this.age ? `${this.age} ${this.$t('customerSearch.years')}` : ''].filter(Boolean).join(', ')
      },
      customerTypes (): string {
        var self = this
        // @ts-ignore - mixins not picked up
        return this.getCustomerTypesTranslations((item) => {
          return _.includes(bitmaskhelper.bitmaskToArray(self.volunteer.friendInfo.standardProfile.customerTypes), item.id)
        }).filter(Boolean).join(', ')
      },
      address (): string {
        if (this.volunteer.friendInfo.standardProfile.hasAlternativeAddress) {
          return addresshelper.formatAddress(this.volunteer.friendInfo.standardProfile.alternativeStreet, this.volunteer.friendInfo.standardProfile.alternativeZipCode, this.volunteer.friendInfo.standardProfile.alternativeCity)
        } else {
          return addresshelper.formatAddress(this.volunteer.streetAddress, this.volunteer.zipCode, this.volunteer.city)
        }
      },
      fastestTransportation (): number | undefined {
        var self = this
        var fastest = [1,
                       2,
                       4,
                       3]
        return _.find(fastest, (item) => {
          return _.includes(self.volunteer.transportation, item)
        })
      },
      transportationText (): string {
        // @ts-ignore - mixins not picked up
        var mode = this.getTransportationTranslation(this.fastestTransportation)
        var time = (this.volunteer.travelTime ? this.volunteer.travelTime : '?') + ' min'
        return [time, mode].filter(Boolean).join(', ')
      },
      availabilityFrequency (): string {
        // @ts-ignore - mixins not picked up
        var frequency = this.getAvailabilityFrequencyTranslation(this.volunteer.friendInfo.standardProfile.availabilityFrequence)
        return frequency
      },
      availabilityText (): string {
        var self = this
        // @ts-ignore - mixins not picked up
        var times = this.getAvailabilityTimesTranslations((item) => {
          // @ts-ignore - mixins not picked up
          return _.includes(bitmaskhelper.bitmaskToArray(self.volunteer.friendInfo.standardProfile.availabilityTimes), item.id)
        }).filter(Boolean).join(', ')
        return times
      },
      friendshipTypes (): string {
        // @ts-ignore - mixins not picked up
        return this.getFriendshipTypesMaskTranslation(this.volunteer.friendInfo.standardProfile.friendshipTypes)
      },
      genderPreferences (): string {
        // @ts-ignore - mixins not picked up
        return this.getGenderPreferencesMaskTranslation(this.volunteer.friendInfo.standardProfile.preferredGenders)
      },
      agePreferences (): string {
        // @ts-ignore - mixins not picked up
        return this.getAgePreferencesMaskTranslation(this.volunteer.friendInfo.standardProfile.preferredAges)
      },
      spokenLanguages (): string {
        var self = this
        var languages = [] as string[]
        _.forEach(this.volunteer.spokenLanguages, (item) => {
          if (item !== self.volunteer.nativeLanguage) {
            // @ts-ignore - mixins not picked up
            languages.push(this.normalizeTranslation(item))
          }
        })
        languages.push(this.volunteer.otherLanguages)
        return languages.filter(Boolean).join(', ')
      },
      interests (): string {
        // @ts-ignore - mixins not picked up
        return this.getInterestsMaskTranslation(this.volunteer.friendInfo.standardProfile.interests)
      },
      contactDetails (): string {
        var phone = this.volunteer.phoneNumber ? `${this.$t('customerSearch.telAbbreviation')} ${this.volunteer.phoneNumber}` : ''
        var email = this.volunteer.email ? `${this.$t('customerSearch.emailAbbreviation')}: ${this.volunteer.email}` : ''
        return [phone, email].filter(Boolean).join(' | ')
      },
      showDistanceWarning (): boolean {
        if (!this.showMatches) {
          return false
        }
        if (this.matches.travelData) {
          return !this.matches.travelData.isRealDistance
        }
        return false
      },
      showDistanceWarningValue (): YesNoWarning {
        return YesNoWarning.Warning
      },
      targetId (): number {
        return this.volunteer.friendInfo.standardProfile.id
      }
    },
    watch: {
      value (newVal): void {
        this.volunteer = newVal
      }
    },
    methods: {
      lastContacted (lastContactedDate: Date | undefined): string {
        if (!lastContactedDate) {
          return `${this.$t('customerSearch.neverContacted')}`
        }
        // @ts-ignore (no mixins found)
        return `${this.$t('customerSearch.lastContacted')} ${finnishDateFormat(lastContactedDate)}`
      },
      moveVolunteer (): void {
        this.$emit('moveVolunteer')
      },
      getUpdateData (): any {
        if (!this.isNonIdent) {
          // @ts-ignore - mixins not picked up
          return this.prune(this.volunteer.friendInfo)
        }
        // @ts-ignore - mixins not picked up
        return this.prune(this.volunteer)
      },
      getUpdateUrl (): string {
        if (!this.isNonIdent) {
          return this.$store.state.apiBases.friendservice + this.$store.state.apiEndpoints.updateidentstandardfriendvolunteer[0]
        }
        return this.$store.state.apiBases.friendservice + this.$store.state.apiEndpoints.updatenonidentstandardfriendvolunteer[0]
      },
      daysWithoutFriend (lastFriendshipDate): string {
        if (!lastFriendshipDate) {
          return `${this.$t('customerSearch.neverHadFriend')}`
        }
        if (this.volunteer.friendInfo.standardProfile.friendships.filter(friendship => friendship.friendshipState === FriendshipState.Active).length > 0) {
          // @ts-ignore (mixin not found)
          return `${this.$t('customerSearch.hasActiveFriendship')}` // we might want to change this to a different message in the future
        }
        // @ts-ignore (no mixins found)
        return `${this.calculateDayDiff(lastFriendshipDate)} ${this.$t('customerSearch.daysWithoutFriend')}`
      },
      notifyRefresh (): void {
        this.$emit('refresh')
      },
      saveVolunteer (): void {
        var self = this
        self.isSending = true
        this.$validator.validateAll().then(result => {
          if (result) {
            var url = this.getUpdateUrl()
            var data = this.getUpdateData()
            this.$http.put(url, data).then(response => {
              if (response.ok) {
                self.isSending = false
              }
            }, response => {
              self.isSending = false
              if (response.bodyText) {
                // @ts-ignore - mixins not picked up
                this.showAlertError(`${this.$t('common.saveFailed')}: ${response.bodyText}`)
              }
              console.error('Error saving volunteer')
            })
          } else {
            // @ts-ignore - mixins not picked up
            this.firstError()
          }
        })
      },
      toggleActivity (): void {
        this.isSending = true
        var uri = this.$store.state.apiBases.friendservice
        if (this.value.friendInfo.standardProfile.state === CustomerState.NonActive) {
          uri += this.$store.state.apiEndpoints.markactivevolunteerstandard[0]
        } else {
          uri += this.$store.state.apiEndpoints.markinactivevolunteerstandard[0]
        }

        uri = uri
          .replace('{volunteerid}', this.value.friendInfo.id)
          .replace('{groupid}', this.group?.id)

        const self = this

        this.$http.post(uri).then(() => {
          self.isSending = false
          this.notifyRefresh()
        })
      }
    }
  })
</script>

<style lang="scss">
  @import "../../assets/scss/_variables.scss";

  .volunteercardstandard {
    margin-bottom: 30px;

    .usericons {
        display: inline-block;
        margin-left: 5px;
        color: $brand-red;
      }

      .topsection {
      min-height: 120px;
      .circle {
        float: left;
        margin-top:20px;
      }
      .contacted {
        text-align: center;
        font-size: 13px;
        margin-top: 15px;
      }
      .details {
        margin-left:70px;

        .name {
          text-transform: uppercase;
          color: $brand-red;
          font-weight: bold;
          position: relative;
          .edit {
            position: absolute;
            left:-15px;
            text-decoration: none;
            color:$brand-red;
          }
          svg {
            margin-left: 5px;
          }
        }

        .personal {
          font-size: 0.8em;
          font-weight:bold;
          .short-description {
            font-weight:normal;
            color:$brand-red;
            font-style: italic;
            margin-top: 10px;
            position:relative;
            span {
              padding: 0 5px;
            }
            svg {
              font-size:12px;
              display:inline-block;
            }
          }
        }
      }
      .nofriend {
        text-align: center;
        font-size: 13px;
        margin-top: 15px;
      }
    }

    .properties {
      clear: both;
      margin-top: 10px;
      margin-bottom: 30px;

      .property {
        display: flex;
        flex-flow: row nowrap;
        justify-content: space-between;
        align-items: center;

        min-height: 55px;
        margin: 0;
        border-bottom: solid 1px $brand-grey3;
        padding: 5px 0;

        .icon {
          flex: 0 0 40px;
          text-align: center;

          font-size: 2em;
          color:$brand-red;
        }
        //IE11
        *::-ms-backdrop, .icon { margin-top: 15px; }

        .content {
          margin-left: 10px;
          flex: auto;

          font-size: 0.8em;
          dl {
            margin-bottom: 0;
            dt {
              font-size: 1em;
            }
          }
        }

        .match {
          flex: 0 0 40px;
          text-align: right;

          font-size: 1.5em;
        }
      }
    }

    .moreinfo {
      .description,
      .contact,
      .other {
        margin-bottom: 20px;
      }
      dl {
        margin-bottom: 0;
        font-size: 0.8em;
        dt {
          font-size: 1em;
        }
      }
    }

    .button-row {
    text-align: center;
    margin-top: 15px;

      .load-indicator {
        width: 100%;
        margin-bottom: 15px;
        color: $brand-red;
      }
      button {
        margin-bottom: 15px;
        &.button-disabled {
          border-color: $brand-grey4;
          color: $brand-grey4;
          cursor: not-allowed;
          outline: none;
          &:hover {
            background-color:$brand-white;
          }
        }
      }
    }
  }
</style>
